import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Platform, TextInput, type TextInputProps } from 'react-native';

/** O conteúdo pode crescer sem esconder o final do texto em colunas estreitas. */
export const ExpandingTextInput = ({ style, onContentSizeChange, onLayout, ...props }: TextInputProps) => {
  const [height, setHeight] = useState(42);
  const hostRef = useRef<TextInput | null>(null);
  const callbacksRef = useRef({ onContentSizeChange, onLayout });
  callbacksRef.current = { onContentSizeChange, onLayout };
  const measureWebHeight = useCallback(() => {
    const node = hostRef.current as unknown as HTMLTextAreaElement | null;
    if (!node?.getClientRects || !node.getClientRects().length ||
      !(node.getBoundingClientRect().width > 0) || typeof getComputedStyle !== 'function') return;
    const computed = getComputedStyle(node);
    const pixels = (value: string) => Number.parseFloat(value) || 0;
    const previousHeight = node.style.height;
    let nextHeight: number;
    try {
      // Liberar a altura anterior permite medir também a redução; minHeight continua valendo.
      node.style.height = '0px';
      const adjustment = computed.boxSizing === 'border-box'
        ? pixels(computed.borderTopWidth) + pixels(computed.borderBottomWidth)
        : -pixels(computed.paddingTop) - pixels(computed.paddingBottom);
      nextHeight = Math.max(42, Math.ceil(node.scrollHeight + adjustment));
    } finally {
      node.style.height = previousHeight;
    }
    if (Number.isFinite(nextHeight)) setHeight(current => current === nextHeight ? current : nextHeight);
  }, []);
  const handleWebContentSizeChange = useCallback<NonNullable<TextInputProps['onContentSizeChange']>>(event => {
    measureWebHeight();
    callbacksRef.current.onContentSizeChange?.(event);
  }, [measureWebHeight]);
  const handleWebLayout = useCallback<NonNullable<TextInputProps['onLayout']>>(event => {
    measureWebHeight();
    callbacksRef.current.onLayout?.(event);
  }, [measureWebHeight]);
  useLayoutEffect(() => {
    if (Platform.OS === 'web') measureWebHeight();
  });
  if (Platform.OS === 'web') return <TextInput {...props} ref={hostRef} multiline scrollEnabled={false} textAlignVertical="top"
    style={[style, { minHeight: 42, height, overflow: 'hidden' }]}
    onContentSizeChange={handleWebContentSizeChange} onLayout={handleWebLayout} />;
  return <TextInput {...props} multiline scrollEnabled={false} textAlignVertical="top" onLayout={onLayout}
    // No iOS, a medição nativa do multiline precisa ficar livre para acompanhar o texto.
    style={[style, { minHeight: 42, ...(Platform.OS === 'ios' ? {} : { height }), overflow: 'hidden' }]}
    onContentSizeChange={event => {
      setHeight(Math.max(42, Math.ceil(event.nativeEvent.contentSize.height)));
      onContentSizeChange?.(event);
    }} />;
};
